import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';

import { usageMonitoringService } from '../services/usageMonitoringService';
import type { UsageGranularity, UsageLineRow, UsageQueryParams } from '../types';

const STALE_MS = 60 * 1000;
const GC_MS = 10 * 60 * 1000;

export const usageMonitoringQueryKey = ['usage-monitoring'] as const;

export interface UsageFilterState {
  dateFrom: string;
  dateTo: string;
  warehouseId: number | null;
  supplierId: number | null;
  userId: number | null;
  organizationId: number | null;
  granularity: UsageGranularity;
  datesReady: boolean;
  detailStatus: string;
  detailPresence: string;
}

export interface UsageDetailDrilldown {
  status?: string | null;
  presence?: string | null;
  userId?: number | null;
  supplierId?: number | null;
  /** Si true, limpia el filtro de usuario/proveedor no indicado. */
  replaceScope?: boolean;
}

interface UsageMonitoringFiltersApi {
  filters: UsageFilterState;
  setDateFrom: (value: string) => void;
  setDateTo: (value: string) => void;
  setWarehouseId: (value: number | null) => void;
  setSupplierId: (value: number | null) => void;
  setUserId: (value: number | null) => void;
  setOrganizationId: (value: number | null) => void;
  setGranularity: (value: UsageGranularity) => void;
  setDetailStatus: (value: string) => void;
  setDetailPresence: (value: string) => void;
  applyDetailDrilldown: (opts: UsageDetailDrilldown) => void;
  applyWindowDefaults: (dateFrom: string, dateTo: string) => void;
  queryParams: UsageQueryParams;
  resetFilters: () => void;
}

const UsageMonitoringFiltersContext = createContext<UsageMonitoringFiltersApi | null>(null);

const initialFilters: UsageFilterState = {
  dateFrom: '',
  dateTo: '',
  warehouseId: null,
  supplierId: null,
  userId: null,
  organizationId: null,
  granularity: 'month',
  datesReady: false,
  detailStatus: '',
  detailPresence: '',
};

export function UsageMonitoringFiltersProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<UsageFilterState>(initialFilters);

  const setDateFrom = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, dateFrom: value, datesReady: true }));
  }, []);

  const setDateTo = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, dateTo: value, datesReady: true }));
  }, []);

  const setWarehouseId = useCallback((value: number | null) => {
    setFilters((prev) => ({ ...prev, warehouseId: value }));
  }, []);

  const setSupplierId = useCallback((value: number | null) => {
    setFilters((prev) => ({ ...prev, supplierId: value }));
  }, []);

  const setUserId = useCallback((value: number | null) => {
    setFilters((prev) => ({ ...prev, userId: value }));
  }, []);

  const setOrganizationId = useCallback((value: number | null) => {
    setFilters((prev) => ({
      ...prev,
      organizationId: value,
      warehouseId: null,
      supplierId: null,
      userId: null,
      dateFrom: '',
      dateTo: '',
      datesReady: false,
      detailStatus: '',
      detailPresence: '',
    }));
  }, []);

  const setGranularity = useCallback((value: UsageGranularity) => {
    setFilters((prev) => ({ ...prev, granularity: value }));
  }, []);

  const setDetailStatus = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, detailStatus: value }));
  }, []);

  const setDetailPresence = useCallback((value: string) => {
    setFilters((prev) => ({ ...prev, detailPresence: value }));
  }, []);

  const applyDetailDrilldown = useCallback((opts: UsageDetailDrilldown) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (opts.status !== undefined) next.detailStatus = opts.status ?? '';
      if (opts.presence !== undefined) next.detailPresence = opts.presence ?? '';
      if (opts.replaceScope) {
        next.userId = opts.userId ?? null;
        next.supplierId = opts.supplierId ?? null;
      } else {
        if (opts.userId !== undefined) next.userId = opts.userId;
        if (opts.supplierId !== undefined) next.supplierId = opts.supplierId;
      }
      return next;
    });
  }, []);

  const applyWindowDefaults = useCallback((dateFrom: string, dateTo: string) => {
    setFilters((prev) => {
      if (prev.datesReady) return prev;
      return { ...prev, dateFrom, dateTo, datesReady: true };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
  }, []);

  const queryParams = useMemo<UsageQueryParams>(() => {
    const params: UsageQueryParams = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.warehouseId != null) params.warehouse = filters.warehouseId;
    if (filters.supplierId != null) params.supplier = filters.supplierId;
    if (filters.userId != null) params.user = filters.userId;
    if (filters.organizationId != null) params.organization = filters.organizationId;
    params.granularity = filters.granularity;
    return params;
  }, [filters]);

  const value = useMemo(
    () => ({
      filters,
      setDateFrom,
      setDateTo,
      setWarehouseId,
      setSupplierId,
      setUserId,
      setOrganizationId,
      setGranularity,
      setDetailStatus,
      setDetailPresence,
      applyDetailDrilldown,
      applyWindowDefaults,
      queryParams,
      resetFilters,
    }),
    [
      filters,
      setDateFrom,
      setDateTo,
      setWarehouseId,
      setSupplierId,
      setUserId,
      setOrganizationId,
      setGranularity,
      setDetailStatus,
      setDetailPresence,
      applyDetailDrilldown,
      applyWindowDefaults,
      queryParams,
      resetFilters,
    ],
  );

  return createElement(UsageMonitoringFiltersContext.Provider, { value }, children);
}

export function useUsageMonitoringFilters() {
  const ctx = useContext(UsageMonitoringFiltersContext);
  if (!ctx) {
    throw new Error('useUsageMonitoringFilters must be used within UsageMonitoringFiltersProvider');
  }
  return ctx;
}

function stableKey(params: UsageQueryParams) {
  return JSON.stringify(params);
}

export function useUsageFiltersQuery(params: UsageQueryParams, enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'filters', stableKey(params)],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getFilters(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useUsageKpisQuery(params: UsageQueryParams, enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'kpis', stableKey(params)],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getKpis(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useUsageEvolutionQuery(params: UsageQueryParams, enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'evolution', stableKey(params)],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getEvolution(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useUsageSupplierDaysQuery(params: UsageQueryParams, enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'supplier-days', stableKey(params)],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getSupplierDays(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useUsageLinesQuery(params: UsageQueryParams, enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'lines', stableKey(params)],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getLines(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

/** Carga todas las páginas de líneas (para totales de pedido / filtro). */
export function useUsageAllLinesQuery(params: UsageQueryParams | null, enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'lines-all', stableKey(params ?? {})],
    enabled: enabled && Boolean(params),
    queryFn: async ({ signal }) => {
      if (!params) return [] as UsageLineRow[];
      const all: UsageLineRow[] = [];
      let pageNum = 1;
      let numPages = 1;
      do {
        const chunk = await usageMonitoringService.getLines(
          { ...params, page: pageNum, page_size: 200 },
          { signal },
        );
        all.push(...chunk.results);
        numPages = chunk.num_pages;
        pageNum += 1;
      } while (pageNum <= numPages);
      return all;
    },
    staleTime: STALE_MS,
    gcTime: GC_MS,
  });
}

export function useUsageByGroupQuery(params: UsageQueryParams, enabled = true) {
  return useQuery({
    queryKey: [
      ...usageMonitoringQueryKey,
      'by-group',
      params.group_by ?? 'user',
      stableKey(params),
    ],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getByGroup(params, { signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    placeholderData: (prev) => prev,
  });
}

export function useUsageOrganizationsQuery(enabled = true) {
  return useQuery({
    queryKey: [...usageMonitoringQueryKey, 'organizations'],
    enabled,
    queryFn: ({ signal }) => usageMonitoringService.getOrganizations({ signal }),
    staleTime: STALE_MS,
    gcTime: GC_MS,
    retry: false,
  });
}
